import React from 'react';
import { observer } from 'mobx-react';
import copyToClipBoard from 'copy-to-clipboard';
import classnames from 'classnames';

import { useAsyncEffect, useObservable, useUser } from 'shared/hooks';
import { UiButton, UiCalendar, UiIcon, UiLoading, UiModalContent, UiTextarea, UiUserBadge } from 'shared/uikit-new';
import { ITaskModel, TaskModel } from 'shared/models/TaskTracker';
import { tasksDeleteQuery, tasksGetQuery, tasksUpdateQuery } from 'shared/queries/TaskTracker';
import { notifier, toDate, toRgba } from 'shared/utilities';
import { CComments } from 'shared/components';
import { COLORS, ROUTES } from 'shared/constants';
import { TaskTrackerTaskStatusEnum } from 'shared/enums';
import { CTaskFile } from 'shared/components/TaskTracker/CTaskFile';
import { CTaskViews } from 'shared/components/TaskTracker/CTaskViews';

import './styles.scss';

type PropsType = {
    taskTrackerTask?: ITaskModel;
    onClose?: () => void;
    onDeleted?: (taskTrackerTask: TaskModel) => void;
    onSaved?: (taskTrackerTask: TaskModel) => void;
    onChangeState?: (state: string) => void;
};

export const MdTaskTrackerTaskShow = observer(function MdTaskTrackerTaskShow({
    taskTrackerTask: taskTrackerTaskData,
    onClose = () => undefined,
    onDeleted = () => undefined,
    onSaved = () => undefined,
    onChangeState = () => undefined,
}: PropsType) {
    const store = useObservable({
        taskTrackerTask: new TaskModel(taskTrackerTaskData),
        isLoading: true,
        expiredAtIsProposing: false,
        expiredAtPropose: null as string | null,
        expiredAtProposeComment: null as string | null,
        expiredAtProposeIsHandling: false,
        expiredAtProposeIsAccepted: true,
        expiredAtProposeIsAcceptedComment: null as string | null,
    });

    const user = useUser();

    useAsyncEffect(async () => {
        store.set('taskTrackerTask', new TaskModel(taskTrackerTaskData));

        if (!store.taskTrackerTask.id) {
            return;
        }

        const response = await tasksGetQuery({ id: store.taskTrackerTask.id });
        if (response.isSuccess && response.data) {
            store.set('taskTrackerTask', new TaskModel(response.data.item));
        }
        store.set('isLoading', false);
    }, [taskTrackerTaskData?.id, store]);

    const handleDelete = async () => {
        const isConfirmed = await notifier().confirm({
            title: 'Подтверждение',
            description: `Подтвердите удаление задачи №${store.taskTrackerTask.id} «${store.taskTrackerTask.name}»`,
        });

        if (isConfirmed) {
            await tasksDeleteQuery({ id: store.taskTrackerTask.id });
            onDeleted(store.taskTrackerTask);
            onClose();
        }
    };

    const handleUpdate = async () => {
        await tasksUpdateQuery(store.taskTrackerTask.toObject());
        onSaved(store.taskTrackerTask);
    };

    const canSetStatusDone = () => {
        if (store.taskTrackerTask.status.in([TaskTrackerTaskStatusEnum.Done, TaskTrackerTaskStatusEnum.Archive])) {
            return false;
        }
        return store.taskTrackerTask.ownerUserId === user.id || store.taskTrackerTask.responsibleUserId === user.id || store.taskTrackerTask.space.spaceOwners.some(spaceOwner => +spaceOwner.userId === +user.id);
    };

    const canEdit = () => {
        if (store.taskTrackerTask.status.in([TaskTrackerTaskStatusEnum.Done, TaskTrackerTaskStatusEnum.Archive])) {
            return false;
        }

        return store.taskTrackerTask.ownerUserId === user.id || store.taskTrackerTask.space.spaceOwners.some(spaceOwner => +spaceOwner.userId === +user.id);
    };

    const canSetStatusDelayed = () => {
        if (store.taskTrackerTask.status.in([TaskTrackerTaskStatusEnum.Done, TaskTrackerTaskStatusEnum.Archive, TaskTrackerTaskStatusEnum.Delayed])) {
            return false;
        }

        return store.taskTrackerTask.ownerUserId === user.id || store.taskTrackerTask.responsibleUserId === user.id || store.taskTrackerTask.space.spaceOwners.some(spaceOwner => +spaceOwner.userId === +user.id);
    };

    const canSetStatusArchive = () => {
        if (!store.taskTrackerTask.status.is(TaskTrackerTaskStatusEnum.Done)) {
            return false;
        }

        return store.taskTrackerTask.ownerUserId === user.id || store.taskTrackerTask.responsibleUserId === user.id || store.taskTrackerTask.space.spaceOwners.some(spaceOwner => +spaceOwner.userId === +user.id);
    };

    const canRestoreFromArchive = () => {
        if (!store.taskTrackerTask.status.is(TaskTrackerTaskStatusEnum.Archive)) {
            return false;
        }

        return store.taskTrackerTask.ownerUserId === user.id || store.taskTrackerTask.responsibleUserId === user.id || store.taskTrackerTask.space.spaceOwners.some(spaceOwner => +spaceOwner.userId === +user.id);
    };

    const canSetStatusInWork = () => {
        if (!store.taskTrackerTask.status.in([TaskTrackerTaskStatusEnum.Delayed])) {
            return false;
        }

        return store.taskTrackerTask.ownerUserId === user.id || store.taskTrackerTask.responsibleUserId === user.id || store.taskTrackerTask.space.spaceOwners.some(spaceOwner => +spaceOwner.userId === +user.id);
    };

    const canDelete = () => {
        return store.taskTrackerTask.ownerUserId === user.id || store.taskTrackerTask.space.spaceOwners.some(spaceOwner => +spaceOwner.userId === +user.id);
    };

    const canPropose = () => {
        if (store.taskTrackerTask.expiredAtPropose) {
            return false;
        }

        if (store.taskTrackerTask.status.in([TaskTrackerTaskStatusEnum.Done, TaskTrackerTaskStatusEnum.Archive])) {
            return false;
        }

        return store.taskTrackerTask.responsibleUserId === user.id || store.taskTrackerTask.users.some(u => u.id === +user.id);
    };

    if (store.isLoading || !store.taskTrackerTask.id) {
        return <UiLoading />;
    }

    return (
        <UiModalContent>
            <div className="md-task-tracker-task-show__name">
                {store.taskTrackerTask.name}
            </div>
            <div className="md-task-tracker-task-show__meta">
                <div className="md-task-tracker-task-show__created-at">
                    Задача №{store.taskTrackerTask.id} от {toDate(store.taskTrackerTask.createdAt).format('D MMMM YYYY, HH:mm')}
                </div>
                {(store.taskTrackerTask.boardId || store.taskTrackerTask.spaceId) && (
                    <div
                        className="md-task-tracker-task-show__board"
                        onClick={() => {
                            copyToClipBoard(window.location.origin + ROUTES.TASK_TRACKER(store.taskTrackerTask.spaceId));
                        }}
                    >
                        {[store.taskTrackerTask.space?.name, store.taskTrackerTask.board?.name].filter(s => !!s).join(' / ')}
                    </div>
                )}
                <CTaskViews
                    className="md-task-tracker-task-show__views"
                    taskViews={store.taskTrackerTask.taskViews}
                />
            </div>
            <div className="md-task-tracker-task-show__inner">
                <div className="md-task-tracker-task-show-expired-at-and-priority">
                    <div className="md-task-tracker-task-show-expired-at-and-priority__main">
                        {store.taskTrackerTask.expiredAt && (
                            <div className={classnames('md-task-tracker-task-show-expired-at', {
                                'md-task-tracker-task-show-expired-at--proposed': store.taskTrackerTask.expiredAtPropose,
                            })}
                            >
                                <UiIcon.CalendarDays size={24} />
                                <span>{toDate(store.taskTrackerTask.expiredAt).format('D MMMM YYYY, HH:mm')}</span>
                                {store.taskTrackerTask.expiredAtProposeIsAccepted === true && (
                                    <div
                                        className="md-task-tracker-task-show-expired-at__badge"
                                        style={{
                                            backgroundColor: '#22c55e',
                                            color: COLORS._FFFFFF,
                                        }}
                                    >
                                        <UiIcon.Check size={16} />
                                        <span>Изменён</span>
                                    </div>
                                )}
                                {store.taskTrackerTask.expiredAtProposeIsAccepted === false && (
                                    <div
                                        className="md-task-tracker-task-show-expired-at__badge"
                                        style={{
                                            backgroundColor: '#e84118',
                                            color: COLORS._FFFFFF,
                                        }}
                                    >
                                        <UiIcon.X size={16} />
                                        <span>Отказано</span>
                                    </div>
                                )}
                            </div>
                        )}
                        {store.taskTrackerTask.expiredAtPropose && (
                            <div className={'md-task-tracker-task-show-expired-at'}>
                                <UiIcon.CalendarDays size={24} />
                                <span>{toDate(store.taskTrackerTask.expiredAtPropose).format('D MMMM YYYY, HH:mm')}</span>
                                {!store.taskTrackerTask.ownerUser?.is(user) && (
                                    <div
                                        className="md-task-tracker-task-show-expired-at__badge"
                                        style={{
                                            backgroundColor: '#fde047',
                                            color: COLORS._15171C,
                                        }}
                                    >
                                        На согласовании
                                    </div>
                                )}
                                {store.taskTrackerTask.ownerUser?.is(user) && (
                                    <div
                                        className="md-task-tracker-task-show-expired-at__badge"
                                        style={{
                                            backgroundColor: '#778ca3',
                                            color: COLORS._FFFFFF,
                                        }}
                                    >
                                        Изменение крайнего срока
                                    </div>
                                )}
                                {(store.taskTrackerTask.ownerUser?.is(user) && !!store.taskTrackerTask.expiredAtProposeComment) && (
                                    <div className="md-task-tracker-task-show-expired-at__comment">
                                        <UiIcon.MessageSquareText size={24} />
                                        <label>{store.taskTrackerTask.expiredAtProposeComment}</label>
                                    </div>
                                )}
                                {store.taskTrackerTask.ownerUser?.is(user) && (
                                    <div
                                        className="md-task-tracker-task-show-expired-at__action"
                                        onClick={() => {
                                            store.update({
                                                expiredAtProposeIsHandling: true,
                                                expiredAtProposeIsAccepted: true,
                                            });
                                        }}
                                    >
                                        Подтвердить
                                    </div>
                                )}
                                {store.taskTrackerTask.ownerUser?.is(user) && (
                                    <div
                                        className="md-task-tracker-task-show-expired-at__action"
                                        onClick={() => {
                                            store.update({
                                                expiredAtProposeIsHandling: true,
                                                expiredAtProposeIsAccepted: false,
                                            });
                                        }}
                                    >
                                        Отказать
                                    </div>
                                )}
                                {store.expiredAtProposeIsHandling && (
                                    <div className="md-task-tracker-task-show-expired-at-acceptance">
                                        <UiTextarea
                                            className="md-task-tracker-task-show-expired-at-acceptance__comment"
                                            value={store.expiredAtProposeIsAcceptedComment}
                                            onChange={store.handleChange}
                                            name="expiredAtProposeIsAcceptedComment"
                                            placeholder={'Добавьте комментарий к решению'}
                                        />
                                        <div className="md-task-tracker-task-show-expired-at-acceptance__actions">
                                            <UiButton
                                                onClick={() => {
                                                    store.taskTrackerTask.update({
                                                        expiredAt: store.expiredAtProposeIsAccepted
                                                            ? store.taskTrackerTask.expiredAtPropose
                                                            : store.taskTrackerTask.expiredAt,
                                                        expiredAtPropose: null,
                                                        expiredAtProposeUserId: null,
                                                        expiredAtProposeComment: null,
                                                        expiredAtProposeIsAccepted: store.expiredAtProposeIsAccepted,
                                                        expiredAtProposeIsAcceptedComment: store.expiredAtProposeIsAcceptedComment,
                                                    });
                                                    handleUpdate();
                                                }}
                                            >
                                                Отправить
                                            </UiButton>
                                            <UiButton
                                                onClick={() => store.set('expiredAtProposeIsHandling', false)}
                                                style={{
                                                    backgroundColor: [COLORS._EAECF1, COLORS._C6CAD1],
                                                    color: [COLORS._2C2F35, COLORS._2C2F35]
                                                }}
                                            >
                                                Отмена
                                            </UiButton>
                                        </div>
                                    </div>
                                )}
                                {store.taskTrackerTask.expiredAtProposeUser?.is(user) && (
                                    <div
                                        className="md-task-tracker-task-show-expired-at__action"
                                        onClick={() => {
                                            store.taskTrackerTask.update({
                                                expiredAtPropose: null,
                                                expiredAtProposeUserId: null,
                                                expiredAtProposeComment: null,
                                            });
                                            handleUpdate();
                                        }}
                                    >
                                        Отменить
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div
                        className="md-task-tracker-task-show__priority"
                        style={{ backgroundColor: toRgba(store.taskTrackerTask.priority.color, 0.2) }}
                    >
                        <i style={{ backgroundColor: store.taskTrackerTask.priority.color }} />
                        <span>{store.taskTrackerTask.priority.name}</span>
                    </div>
                </div>

                {store.taskTrackerTask.description && (
                    <div className="md-task-tracker-task-show__description">
                        {store.taskTrackerTask.description}
                    </div>
                )}
                {store.taskTrackerTask.files.length > 0 && (
                    <div className="md-task-tracker-task-show__media">
                        {store.taskTrackerTask.files.map(file => (
                            <CTaskFile key={file} name={file} url={file} />
                        ))}
                    </div>
                )}
                {store.taskTrackerTask.ownerUser && (
                    <div className="md-task-tracker-task-show-users">
                        <div className="md-task-tracker-task-show-users__label">Постановщик</div>
                        <div className="md-task-tracker-task-show-users__items">
                            <UiUserBadge user={store.taskTrackerTask.ownerUser} />
                        </div>
                    </div>
                )}
                {store.taskTrackerTask.responsibleUser && (
                    <div className="md-task-tracker-task-show-users">
                        <div className="md-task-tracker-task-show-users__label">Ответственный</div>
                        <div className="md-task-tracker-task-show-users__items">
                            <UiUserBadge user={store.taskTrackerTask.responsibleUser} />
                        </div>
                    </div>
                )}
                {store.taskTrackerTask.users.length > 0 && (
                    <div className="md-task-tracker-task-show-users">
                        <div className="md-task-tracker-task-show-users__label">Участники</div>
                        <div className="md-task-tracker-task-show-users__items">
                            {store.taskTrackerTask.users.map(user => (
                                <UiUserBadge
                                    key={user.id}
                                    user={user}
                                />
                            ))}
                        </div>
                    </div>
                )}
                <div className="md-task-tracker-task-show__actions">
                    {canSetStatusDone() && (
                        <UiButton
                            template="medium"
                            onClick={() => {
                                store.taskTrackerTask.update({
                                    statusId: TaskTrackerTaskStatusEnum.Done.id,
                                });
                                handleUpdate();
                            }}
                        >
                            Завершить
                        </UiButton>
                    )}
                    {canSetStatusDelayed() && (
                        <UiButton
                            template="medium"
                            onClick={() => {
                                store.taskTrackerTask.update({
                                    statusId: TaskTrackerTaskStatusEnum.Delayed.id,
                                });
                                handleUpdate();
                            }}
                            style={{
                                backgroundColor: ['transparent', 'transparent'],
                                color: [COLORS._858991, COLORS._15171C],
                                borderColor: [COLORS._858991, COLORS._15171C],
                            }}
                        >
                            Отложить
                        </UiButton>
                    )}
                    {canSetStatusInWork() && (
                        <UiButton
                            template="medium"
                            onClick={() => {
                                store.taskTrackerTask.update({
                                    statusId: TaskTrackerTaskStatusEnum.InWork.id,
                                });
                                handleUpdate();
                            }}
                            style={{
                                backgroundColor: ['transparent', 'transparent'],
                                color: [COLORS._858991, COLORS._15171C],
                                borderColor: [COLORS._858991, COLORS._15171C],
                            }}
                        >
                            Взять в работу
                        </UiButton>
                    )}
                    {canPropose() && (
                        <UiButton
                            template="link"
                            onClick={() => {
                                store.update({
                                    expiredAtIsProposing: true,
                                    expiredAtPropose: store.taskTrackerTask.expiredAtPropose || store.taskTrackerTask.expiredAt,
                                });
                            }}
                            style={{
                                backgroundColor: [COLORS.TRANSPARENT, COLORS.TRANSPARENT],
                                color: [COLORS._9EA2AA, COLORS._474A50],
                            }}
                        >
                            <UiIcon.PencilLine size={20} />
                            <span>Изменить крайний срок</span>
                        </UiButton>
                    )}
                    {canEdit() && (
                        <UiButton
                            template="link"
                            onClick={() => onChangeState('save')}
                            style={{
                                backgroundColor: [COLORS.TRANSPARENT, COLORS.TRANSPARENT],
                                color: [COLORS._9EA2AA, COLORS._474A50],
                            }}
                        >
                            <UiIcon.PencilLine size={20} />
                            <span>Редактировать</span>
                        </UiButton>
                    )}
                    {canSetStatusArchive() && (
                        <UiButton
                            template="link"
                            onClick={() => {
                                store.taskTrackerTask.update({
                                    statusId: TaskTrackerTaskStatusEnum.Archive.id,
                                });
                                handleUpdate();
                                onDeleted(store.taskTrackerTask);
                            }}
                            style={{
                                backgroundColor: [COLORS.TRANSPARENT, COLORS.TRANSPARENT],
                                color: [COLORS._9EA2AA, COLORS._474A50],
                            }}
                        >
                            <UiIcon.PackageOpen size={20} />
                            <span>В архив</span>
                        </UiButton>
                    )}
                    {canRestoreFromArchive() && (
                        <UiButton
                            template="link"
                            onClick={() => {
                                store.taskTrackerTask.update({
                                    statusId: TaskTrackerTaskStatusEnum.InWork.id,
                                });
                                handleUpdate();
                            }}
                            style={{
                                backgroundColor: [COLORS.TRANSPARENT, COLORS.TRANSPARENT],
                                color: [COLORS._9EA2AA, COLORS._474A50],
                            }}
                        >
                            <UiIcon.ArchiveRestore size={20} />
                            <span>Восстановить из архива</span>
                        </UiButton>
                    )}
                    {canDelete() && (
                        <UiButton
                            template="link"
                            style={{
                                backgroundColor: [COLORS.TRANSPARENT, COLORS.TRANSPARENT],
                                color: [COLORS._E84118, COLORS._E84118],
                            }}
                            onClick={handleDelete}
                        >
                            <UiIcon.Trash2 size={20} />
                            <span>Удалить</span>
                        </UiButton>
                    )}
                </div>
                {store.expiredAtIsProposing && (
                    <div className="md-task-tracker-task-show-expired-at-proposition">
                        <div className="md-task-tracker-task-show-expired-at-proposition__inner">
                            <UiCalendar
                                withTime
                                hideTimeInputs
                                className="md-task-tracker-task-show-expired-at-proposition__calendar"
                                onChange={store.handleChange}
                                name={'expiredAtPropose'}
                                value={store.expiredAtPropose}
                            />
                            <textarea
                                placeholder="Опишите причину изменения крайнего срока"
                                className="md-task-tracker-task-show-expired-at-proposition__comment"
                                value={store.expiredAtProposeComment || ''}
                                onChange={(e) => {
                                    store.update({
                                        expiredAtProposeComment: e.target.value || null,
                                    });
                                }}
                            />
                        </div>
                        <div className="md-task-tracker-task-show-expired-at-proposition__actions">
                            <UiButton
                                isDisabled={store.taskTrackerTask.expiredAt === store.expiredAtPropose}
                                onClick={() => {
                                    store.taskTrackerTask.update({
                                        expiredAtPropose: store.expiredAtPropose,
                                        expiredAtProposeUserId: user.id,
                                        expiredAtProposeUser: user,
                                        expiredAtProposeComment: store.expiredAtProposeComment,
                                    });
                                    store.update({
                                        expiredAtIsProposing: false,
                                        expiredAtPropose: null,
                                        expiredAtProposeComment: null,
                                    });
                                    handleUpdate();
                                }}
                            >
                                Запросить изменение
                            </UiButton>
                            <UiButton
                                onClick={() => {
                                    store.set('expiredAtIsProposing', false);
                                }}
                            >
                                Отмена
                            </UiButton>
                        </div>
                    </div>
                )}
            </div>
            <CComments
                entityId={store.taskTrackerTask.id}
                entityType="TaskTrackerTask"
            />
        </UiModalContent>
    );
});
